import { CheckIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/solid';
import {
  FetcherWithComponents,
  ShouldReloadFunction,
  useLoaderData,
  useOutletContext,
  useSearchParams
} from '@remix-run/react';
import {
  DataFunctionArgs,
  MetaFunction,
  json,
} from '@remix-run/server-runtime';
import { useEffect, useRef, useState } from 'react';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { APP_META_TITLE } from '~/constants';
// import { FetcherWithComponents } from '~/types';

import Alert from '~/components/Alert';
import PhotoGallery from '~/components/photo-gallery/PhotoGallery';
import { OptionsSelect } from '~/components/products/OptionsSelect';
import { Price } from '~/components/products/Price';
import TopReviews from '~/components/products/TopReviews';
import { getSolutionBySlug } from '~/generated/bp-api';
import { ErrorCode, ErrorResult } from '~/generated/graphql';
import { sessionStorage } from '~/sessions.server';
import { productHasPrice } from '~/utils/ecom-helpers';
import { CartLoaderData } from '../api/active-order';
import { Tab } from '@headlessui/react';
import { classNames } from '~/utils/class-names';
import { usePreview } from '~/generated/tds/bp-utils';
import toast from 'react-hot-toast';
import { OptionCondition } from "@powerschool/solutions-api/core/schema";
import { doArraysOverlap } from '~/generated/tds/utils';

export const meta: MetaFunction = ({ data }) => {
  return data?.app ? {
    title: `${data.app.name} - ${APP_META_TITLE}`,
    description: data.app.excerpt,
    "og:image": data.app.featuredImage

  } : {
    title: APP_META_TITLE
  };
};

export async function loader({ params, request }: DataFunctionArgs) {
  // const { Application } = await getApplicationBySlug(params.slug!, { request });
  let appRecord = await getSolutionBySlug(params.slug!)
  appRecord = usePreview(appRecord, '544x306');
  let app = appRecord?.attributes;
  if (app.featuredImage) {
    app.featuredImage.featured = true;
  }
  let screenshots = app.featuredImage ?  [app.featuredImage]  : [];
  if (app.images?.length) {
    screenshots = screenshots.concat(app.images);
  }
  app = {
    ...app,
    features: app?.features,
    reviews: app?.reviews,
    options: app?.options,
    screenshots: screenshots,
    isCertified: true  // Should eventually come from data
  };

  if (!app) {
    throw new Response('Not Found', {
      status: 404,
    });
  }

  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const preselectedSkus = searchParams.getAll("sku") || '';
  const preselectedIds = searchParams.getAll("id") || '';
  const session = await sessionStorage.getSession(
    request?.headers.get('Cookie'),
  );
  const error = session.get('activeOrderError');
  return json(
    { app, error, preselectedSkus, preselectedIds },
    {
      headers: {
        'Set-Cookie': await sessionStorage.commitSession(session),
      },
    },
  );
}

export const unstable_shouldReload: ShouldReloadFunction = () => true;

export default function ApplicationSlug() {
  const { app, error, preselectedSkus, preselectedIds } = useLoaderData<typeof loader>();

  if (!app) {
    return <div>App not found!</div>;
  }

  const [currentTab, setCurrentTab] = useState(0);

  const tabs = [
    {
      id: 'overview', label: 'Overview',
    }
  ]

  if (app.features) {
    tabs.push({
      id: 'features', label: 'Features'
    })
  }

  if (app.reviews) {
    tabs.push({
      id: 'reviews', label: 'Reviews'
    })
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <div>
      <div className="max-w-6xl mx-auto ml-2">
        <h2 className="text-3xl sm:text-5xl font-light tracking-tight text-gray-900 mb-4">
          {app.name}
        </h2>
        <Breadcrumbs
          items={
            [{ slug: 'all', name: 'Solutions', id: 'all' }]
          }
        ></Breadcrumbs>
        <div className="h-2"></div>
        <Tab.Group selectedIndex={currentTab} onChange={setCurrentTab}>
          <Tab.List className="flex flex-wrap -mb-px mt-4">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                className={({ selected }) =>
                  classNames(
                    'focus:outline-none rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300',
                    selected ? 'active text-blue-600 border-b-2 border-blue-600  dark:text-blue-500 dark:border-blue-500' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                  )
                }
              >
                {tab.label}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="mt-2 mb-8">
            {Object.values(tabs).map((t: any, i) => (
              <Tab.Panel
                key={i}
              >
                {t.id === 'overview' ? <Overview app={app} error={error} preselectedSkus={preselectedSkus} preselectedIds={preselectedIds} /> : ''}
                {t.id === 'features' ? <Features app={app} /> : ''}
                {t.id === 'reviews' ? <Reviews app={app} /> : ''}

              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>

    </div >
  );
}

export function CatchBoundary() {
  return (
    <div className="max-w-6xl mx-auto px-4">
      <h2 className="text-3xl sm:text-5xl font-light tracking-tight text-gray-900 my-8">
        Application not found!
      </h2>
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start mt-4 md:mt-12">
        {/* Image gallery */}
        <div className="w-full max-w-2xl mx-auto sm:block lg:max-w-none">
          <span className="rounded-md overflow-hidden">
            <div className="w-full h-96 bg-slate-200 rounded-lg flex content-center justify-center">
              <PhotoIcon className="w-48 text-white"></PhotoIcon>
            </div>
          </span>
        </div>

        {/* Application info */}
        <div className="mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0">
          <div className="">We couldn't find any Application at that address!</div>
          <div className="flex-1 space-y-3 py-1">
            <div className="h-2 bg-slate-200 rounded"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-2 bg-slate-200 rounded col-span-2"></div>
                <div className="h-2 bg-slate-200 rounded col-span-1"></div>
              </div>
              <div className="h-2 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getAddItemToOrderError(error?: ErrorResult): string | undefined {
  if (!error || !error.errorCode) {
    return undefined;
  }
  switch (error.errorCode) {
    case ErrorCode.OrderModificationError:
    case ErrorCode.OrderLimitError:
    case ErrorCode.NegativeQuantityError:
    case ErrorCode.InsufficientStockError:
      return error.message;
  }
}

function Features({ app }: any) {
  return (
    <ul className="max-w-md space-y-1 text-gray-500 list-disc list-inside dark:text-gray-400">
      {
        app.features?.map((feature: string, index: number) => (
          <li key={index} dangerouslySetInnerHTML={{ __html: feature }}></li>
        ))
      }
    </ul>
  )
}

function Reviews({ app }: any) {
  return (
    <TopReviews reviews={app.reviews}></TopReviews>
  )
}


function Overview({ app, error, preselectedSkus, preselectedIds }: any) {
  let { activeOrderFetcher, activeOrder, setIsCartOpen } = useOutletContext<{
    activeOrderFetcher: FetcherWithComponents<CartLoaderData>;
    activeOrder: any,
    setIsCartOpen: (isOpen: boolean) => void
  }>();

  const [line, setLine] = useState<{ id: string, quantity: number, options: any[], canUpdate: boolean } | undefined>(undefined);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const priceWithTax = 0;
  const currencyCode = 'usd';
  const qtyInCart = line?.quantity ?? 0;

  const addItemToOrderError = getAddItemToOrderError(error);

  function usePrevious(value: any) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const previousStateOfOrder: any = usePrevious(activeOrder);

  useEffect(() => {
    const previousLineItems = previousStateOfOrder?.lines?.length;
    const currentLineItems = activeOrder?.lines?.length;

    if (currentLineItems > previousLineItems) {
      toast.success('Solution has been added to quote! Thank you!');

      if (app.allowMultipleInstancesPerOrder) {
        let newSP = new URLSearchParams(searchParams);
        if (newSP.has("id") || newSP.has("sku")) {
          newSP.delete("id");
          newSP.delete("sku");
          setSearchParams(newSP, { preventScrollReset: true });
        }
      }
      
    }

    if (!app.allowMultipleInstancesPerOrder) {
      const currentLine = activeOrder?.lines.find((l: any) => l.id.indexOf(app.id) > -1);
      if (currentLine) {
        currentLine.canUpdate = false;
      }
      setLine(currentLine);
    } else {
      setLine(undefined);
    }
  }, [activeOrder]);

  const isInArray = (arr: Array<string>, str: string) => {
    return arr.some(a => a === str);
  }

  const getSelected = (line: any, option: any) => {
    const isMulti = option.type === 'multiSelect';
    const existingLineItem = line?.options.find((lo: any) => lo.optionId === option.id)
    if (existingLineItem?.value && line?.canUpdate) {
      return existingLineItem.value;
    }
    const selected = (preselectedSkus?.length ? (option.values.find((lo: any) => isInArray(preselectedSkus, lo.sku)) ? option.values.find((lo: any) => isInArray(preselectedSkus, lo.sku)) : '') : '') ||
                     (preselectedIds?.length ? (option.values.find((lo: any) => isInArray(preselectedIds, lo.id)) ? option.values.find((lo: any) => isInArray(preselectedIds, lo.id)) : '') : '');

    if (selected && line && !line.canUpdate) {
      const updatedLine = { ...line };
      updatedLine.canUpdate = true;
      setLine(updatedLine);
    }

    if (!isMulti) {
      if (existingLineItem?.value) {
        return existingLineItem.value;
      } else {
        return selected || '';
      }
    } else {
      const selectedItems = existingLineItem?.value?.length ? existingLineItem.value : [];
      if (selected && !selectedItems.find((s: any) => s.id === selected.id)) {
        selectedItems.push(selected)
      }
      return selectedItems;
    }
  }

  const updateLine = (option: any, selected: any) => {
    let updatedLine;
    if (!line) {
      updatedLine = {
        id: '',
        quantity: 0,
        options: [] as any[],
        canUpdate: false
      }
    } else {
      updatedLine = { ...line }
    }
    let lineOption = updatedLine.options.find((o: any) => o.optionId === option.id);
    if (lineOption) {
      lineOption.value = selected;
    } else {
      updatedLine.options.push({
        optionId: option.id,
        optionLabel: option.id,
        value: selected
      });
    }

    updatedLine.canUpdate = true;
    setLine(updatedLine);
  }

  const isConditionSatisfied = (condition: OptionCondition) => {
    if (!line) {
      return false;
    }
    const conditionOption = app.options.find(o => o.id === condition.optionId);
    let lineOption = line?.options?.find((o: any) => o.optionId === condition.optionId);
    if (!lineOption) {
      return false;
    }

    let values = Array.isArray(lineOption.value) ? lineOption.value : [ lineOption.value ]; // To account for Selects and Multi-Selects
    if (!conditionOption) {
      return false;
    }
    if (condition.operator === 'EQUALS') {
      return doArraysOverlap(condition.values.map(v => v.id), values.map(v => v.id));
    } else if (condition.operator === 'NOT_EQUALS') {
      return !doArraysOverlap(condition.values.map(v => v.id), values.map(v => v.id));
    }
    return true;
  }

  const updateConditionalQuestions = () => {
    let updatedOptions = [];
    if (app?.options?.length) {
      updatedOptions = app.options.filter(o => {
        if (!o.showConditionally || !o.conditions?.length) {
          return true;
        }
        for (let condition of o.conditions) {
          if (!isConditionSatisfied(condition)) {
            return false;
          }
        }
        return true;
      })
    }
    setFilteredOptions(updatedOptions);
  }

  useEffect(() => {
    updateConditionalQuestions();
  }, [app?.options, line])

  return (
    <div data-tab-text="Overview" data-tab-id="tab-1">
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start mt-4 md:mt-12">
        {/* Image gallery */}
        <PhotoGallery images={app.screenshots}></PhotoGallery>

        {/* Application info */}
        <div className="mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0">
          <div className="">
            <h3 className="sr-only">Description</h3>

            <div
              className="text-base text-gray-700"
              dangerouslySetInnerHTML={{
                __html: app.content,
              }}
            />
            <activeOrderFetcher.Form method="post" action={'/api/active-order'}>
              {line?.id && <input type="hidden" name="lineId" value={line?.id} />}

              <input
                type="hidden"
                name="variantId"
                value={app.id}
              ></input>
              <input
                type="hidden"
                name="slug"
                value={app.slug}
              ></input>
              <input
                type="hidden"
                name="allowMultipleInstancesPerOrder"
                value={app.allowMultipleInstancesPerOrder}
              ></input>
              {filteredOptions?.length > 0 && <div className="pt-4">
                {filteredOptions.map((o: any, index: number) =>
                  <div className="mb-2" key={index}>
                    <OptionsSelect {...o}
                      option={o}
                      optionIndex={index}
                      selected={getSelected(line, o)}
                      required={o.isRequired}
                      tooltip={o.tooltip}
                      isMulti={o.type === "multiSelect"}
                      onUpdate={(selected: any) => updateLine(o, selected)} />
                  </div>
                )}
              </div>}
              <div className="mt-10 flex flex-col sm:flex-row sm:items-center">
                {productHasPrice(app) ?? (<p className="text-3xl text-gray-900 mr-4">
                  <Price
                    priceWithTax={priceWithTax}
                    currencyCode={currencyCode}
                  ></Price>
                </p>)
                }
                <div className="flex sm:flex-col1 align-baseline">
                  {!line?.id ? <button
                    type="submit" name="action" value="addItemToOrder"
                    className={`max-w-xs flex-1 text-lg ${activeOrderFetcher.state !== 'idle'
                      ? 'bg-gray-400'
                      : 'bg-primary hover:bg-sky-500'
                      }
                                     transition-colors border border-transparent py-3 px-8 flex items-center
                                      justify-center text-base font-medium text-white focus:outline-none
                                      focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-primary-500 sm:w-full`}
                    disabled={activeOrderFetcher.state !== 'idle'}
                  >
                    Add to Quote
                  </button> : ''}

                  {line?.id && !line?.canUpdate ? <button
                    type="button" onClick={() => setIsCartOpen(true)}
                    className="text-base font-medium ml-4 text-white bg-green-500 active:bg-green-700 hover:bg-green-700"
                    ><CheckIcon className="w-5 h-5 mr-1" />In quote</button> : ''}

                  {line?.id && line?.canUpdate ? <button
                    type="submit" name="action" value="adjustLine"
                    className="text-base font-medium ml-4 text-white bg-green-500 active:bg-green-700 hover:bg-green-700"
                  >Update Quote</button> : ''}

                  {line?.id && line?.canUpdate ? <button
                    type="submit" name="action" value="restoreForm"
                    className="text-base font-medium ml-4 text-white bg-yellow-500 active:bg-yellow-700 hover:bg-yellow-700"
                  >Cancel</button> : ''}

                  {line?.id ? <button
                    type="submit" name="action" value="removeItem"
                    className="ml-4 text-white bg-red-500 active:bg-red-700 hover:bg-red-700"
                  ><TrashIcon /></button> : ''}
                </div>
              </div>

              {addItemToOrderError && (
                <div className="mt-4">
                  <Alert message={addItemToOrderError} />
                </div>
              )}

            </activeOrderFetcher.Form>

            {app.isCertified ? (<div className="mt-8 container certified">
              <div className="flex flex-row items-center certified-badge">
                <img className="certified-image" src="/assets/img/certified.png" />
                <div className="flex flex-col items-start justify-start">
                  <span className="certified-by">Certified By</span>
                  <span className="text-primary font-bold text-xl">PowerSchool</span>
                </div>
              </div>
              <span className="tooltiptext">
                Certified by PowerSchool signifies that a product or service has undergone rigorous security and performance testing,
                meeting the stringent standards set by PowerSchool, a trusted leader in education technology.
              </span>
            </div>) : ''}
          </div>

        </div>
      </div>
    </div>
  )
}